import { bulkSendMessageReadReceipts, createConversationWithEventMessage,sendConversationEventMessage } from 'api/conversation'
import store, { getStoreState } from 'config/store'
import { triggerCampaignActionAPIEvent } from 'modules/api/events'
import { getActiveCampaignId } from 'modules/campaigns/selectors'
import { receiveReadMessages } from 'modules/conversations/actions'
import { setActiveConversation } from 'modules/conversations/actions'
import { isPrerenderedConversation } from 'modules/conversations/utils'
import { getIntegrationIdentityContext } from 'modules/identity'
import { head, isNil, pathOr, values } from 'ramda'
import { ConversationEventType, ConversationId } from 'types/conversation'
import { Conversation } from 'types/conversation'
import { Message } from 'types/message'
import { error, isNilOrEmpty } from 'utils'
import { orderConversationLastMessagesBy } from 'utils/conversation'

import { getActiveConversationId } from './selectors'

export const createConversationEvent = async (eventType: ConversationEventType, context: Record<string, any> = {}) => {
  switch(eventType) {
    case ConversationEventType.LEAD_CAPTURED:
      return await createLeadCapturedConversationEvent(context)
    case ConversationEventType.CLICKED:
    case ConversationEventType.SUBMITTED:
    case ConversationEventType.DISMISSED:
    case ConversationEventType.OPENED:
      const conversationActionMessage: Message = await captureConversationActionEvent(eventType, context)
      const campaignId = pathOr(null, ['attributes', 'campaignId'], conversationActionMessage)
      triggerAPIEventForConversationAction(campaignId, eventType)
      return conversationActionMessage
    default:
      break
  }
}

export const triggerAPIEventForConversationAction = (
  campaignId: number,
  eventType: ConversationEventType
) => {
  if (!campaignId) {
    return
  }

  triggerCampaignActionAPIEvent(eventType, campaignId)
}

/**
 * Handles sending of conversation events via network call
 * If the payload contains a conversationId, we'll attach to the current conversation, otherwise we'll create a new conversation with just the event (for pre-rendered announcement playbooks)
 * @param eventPayload - contains payload information about the event
 */
export const sendConversationEvent = async (eventPayload: Record<string, any>) => {
  const activeCampaignId = getActiveCampaignId(getStoreState())

  if (isNilOrEmpty(eventPayload)) {
    return
  }

  try {
    return !!eventPayload.conversationId
      ? await sendConversationEventMessage(eventPayload)
      : await createConversationWithEventMessage(eventPayload, { campaignId: activeCampaignId })
  } catch(err) {
    error('Failed to send conversation event', eventPayload)

  }
}

const createLeadCapturedConversationEvent = async (context: Record<string, any>) => {
  if (!context.email) { return }

  const eventPayload = {
    conversationId: context.conversationId,
    type: ConversationEventType.LEAD_CAPTURED,
    email: context.email,
    leadCapture: {
      email: context.email,
      integrations: getIntegrationIdentityContext(),
      context: _hydrateConversationEventContext()
    },
  }

  const { data } = await sendConversationEvent(eventPayload)
  return data
}

const captureConversationActionEvent = async (type: ConversationEventType, context: Record<string, any> = {}) => {
  if (!type || isNil(context)) { return }

  const eventPayload = {
    type,
    ...context
  }

  const { data } = await sendConversationEvent(eventPayload)
  return data
}

const _hydrateConversationEventContext = () => {
  const { session } = getStoreState()
  const { userAgent, locale, timezone, page = {} } = session?.context || {}

  return {
    page,
    locale,
    timezone,
    userAgent
  }
}

/**
 * Shows the most recent conversation that was created during the session as fallback content
 * if the visitor is not in an active conversation or campaign
 * @returns (boolean) - stating whether or not a conversation was shown
 */
export const showLatestSessionConversation = async (): Promise<boolean> => {
  const state = getStoreState()
  const activeCampaignId = getActiveCampaignId(state)
  const sessionStartedTimestamp = pathOr(null, ['session','visitInformation', 'currentSessionStartedAt'], state)

  if (!!activeCampaignId || !!state.conversations.activeConversation || !sessionStartedTimestamp) {
    return false
  }

  const sessionConversations = values(state.conversations.conversations)
    .map(conversationObject => conversationObject['conversation'])
    .filter(conversation => conversation.createdAt >= sessionStartedTimestamp)

  const mostRecentConversation = getMostRecentConversation(sessionConversations)
  if (!mostRecentConversation) {
    return false
  }

  store.dispatch(setActiveConversation({ conversationId: mostRecentConversation.id }))
  return true
}


export const getMostRecentConversation = (conversations: Conversation[]): Conversation => {
  const mostRecentConversations = orderConversationLastMessagesBy(conversations, 'createdAt')
  return head(mostRecentConversations)
}

/**
 * Sends a fire-and-forget notification to the server that the message should be marked as read
 * @param conversationId - Conversation to mark the message as read from, by id
 * @param messageId  - Message to be marked as read, by id
 */
export const markMessageAsRead = async (conversationId: ConversationId, messageId: number) => {
  try {
    if (!isPrerenderedConversation(conversationId)) {
      await bulkSendMessageReadReceipts(conversationId, [messageId])
    }
    store.dispatch(receiveReadMessages({ conversationId: conversationId }))
  } catch (err) {
    error(new Error(`Failed to mark message as read`), err)
  }
}


export const markActiveConversationAsRead = () => {
  const activeConvoId = getActiveConversationId(getStoreState())
  markConversationAsRead(activeConvoId)
}

/**
 * Sends a fire-and-forget notification to the server that the conversations messages should be marked as read
 * If a conversation is pre-rendered, don't bother updating since it doesn't yet have a real conversation id
 * @param conversationId - Conversation to be marked read, by id
 */
export const markConversationAsRead = (conversationId: ConversationId) => {
  if (!conversationId) {
    return
  }

  const { conversations } = getStoreState()

  const unreadMessages = conversations?.unreadAgentMessages[conversationId] || []

  if (!!unreadMessages.length) {
    try {
      if (!isPrerenderedConversation(conversationId)) {
        bulkSendMessageReadReceipts(conversationId, unreadMessages)
      }
      store.dispatch(receiveReadMessages({ conversationId: conversationId }))
    } catch (err) {
      error(new Error(`Failed to mark conversation as read`), err)
    }
  }
}
